import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, PencilSimple, Trash, X, CircleNotch, House, Buildings, Tent, Broadcast, Info, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

// Property type dropdown — extended list based on user feedback: added
// "guesthouse", "resort", "boutique_hotel" so users can classify all common
// Croatian rental types (kućica u kampu, obiteljski hotel, boutique smještaj).
const TYPE_LABELS = {
  apartment: "Apartmani",
  hotel: "Hotel",
  boutique_hotel: "Boutique hotel",
  hostel: "Hostel",
  guesthouse: "Pansion / Sobe",
  bnb: "Bed & Breakfast",
  villa: "Vila",
  camp: "Kamp",
  resort: "Resort",
  cottage: "Kućica / Bungalov",
};
const TYPE_ICONS = {
  apartment: House,
  hotel: Buildings,
  boutique_hotel: Buildings,
  hostel: Buildings,
  guesthouse: House,
  bnb: House,
  villa: House,
  camp: Tent,
  resort: Buildings,
  cottage: House,
};

// Small "?" tooltip that reveals helper copy on hover — keeps the form clean
// while still educating first-time users about what the field affects.
function InfoTip({ text }) {
  return (
    <span className="group relative ml-1 inline-flex cursor-help align-middle text-muted-foreground" data-testid="infotip">
      <Info size={12} weight="duotone" />
      <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 hidden w-64 -translate-x-1/2 rounded-md border border-border bg-popover px-3 py-2 text-[11px] font-normal normal-case tracking-normal text-foreground shadow-lg group-hover:block">
        {text}
      </span>
    </span>
  );
}

function L({ label, help, children }) {
  return (
    <label className="block">
      <span className="mb-1 flex min-w-0 items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground">
        <span className="truncate">{label}</span>
        {help && <InfoTip text={help} />}
      </span>
      {children}
    </label>
  );
}

// Property create/edit form — now presented as a CENTERED modal (not a right
// drawer) per user preference. Sections: Osnovno / Lokacija / Marketing (opisi
// se prikazuju na Booking Engine-u i budućem javnom sajtu) / Vrijeme dolaska
// i odlaska / Dobi gostiju. Voditelj obrade block je uklonjen — te podatke se
// upisuje isključivo u eVisitor tabu (koji ih koristi + na Politici privatnosti).
function PropertyForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      name: "",
      type: "apartment",
      city: "Rijeka",
      country: "HR",
      currency: "",
      address: "",
      latitude: null,
      longitude: null,
      main_image: "",
      description: { hr: "", en: "" },
      check_in_time: "15:00",
      check_out_time: "10:00",
      display_order: 0,
    }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const isNew = !initial;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateDesc = (lang, v) => setForm((f) => ({ ...f, description: { ...(f.description || {}), [lang]: v } }));

  useEffect(() => {
    api.get("/meta/currencies")
      .then((r) => setCurrencies(r.data?.currencies || []))
      .catch(() => setCurrencies([]));
  }, []);

  const submit = async () => {
    setError("");
    if (!form.name?.trim()) { setError("Naziv je obavezan"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/properties", form);
        onSaved(data);
      } else {
        const { data } = await api.put(`/properties/${initial.id}`, form);
        onSaved(data);
      }
      onClose();
    } catch (e) { setError(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <>
      <div className="rw-sheet-backdrop fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="rw-sheet-mobile fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-border bg-card shadow-2xl sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[92dvh] sm:w-[min(720px,95vw)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl"
        data-testid="property-modal"
      >
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/25 sm:hidden" />
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-base font-semibold text-foreground">{isNew ? "Novi smještaj" : "Uredi smještaj"}</h3>
          <button onClick={onClose} className="rounded p-1.5 text-muted-foreground hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto overscroll-contain p-4 sm:p-5">
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}

          {/* Osnovno */}
          <section className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Osnovno</p>
            <L label="Naziv"><input className="rw-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="prop-name" /></L>
            <L label="Naslov u check-in formi (vidi gost)">
              <input className="rw-input" value={form.checkin_title || ""} onChange={(e) => update("checkin_title", e.target.value)} placeholder="Prazno = naziv brenda" data-testid="prop-checkin-title" />
            </L>
            <div className="grid grid-cols-2 gap-3">
              <L label="Tip">
                <select className="rw-input" value={form.type} onChange={(e) => update("type", e.target.value)} data-testid="prop-type">
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </L>
              <L label="Grad"><input className="rw-input" value={form.city || ""} onChange={(e) => update("city", e.target.value)} /></L>
            </div>
            <L
              label="Valuta"
              help="Valuta u kojoj prodaješ ovaj smještaj na OTA-ima (Booking.com, Airbnb, ...). Ostavi prazno da naslijedi zadanu valutu naloga. Preporučujemo isti kod (npr. USD) za sve rate planove i channel manager mapiranja."
            >
              <select
                className="rw-input"
                value={form.currency || ""}
                onChange={(e) => update("currency", e.target.value || null)}
                data-testid="prop-currency"
              >
                <option value="">— zadano po nalogu —</option>
                {currencies.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} · {c.name} {c.symbol && `(${c.symbol})`}</option>
                ))}
              </select>
            </L>
          </section>

          {/* Lokacija */}
          <section className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Lokacija</p>
            <L
              label="Adresa"
              help="Puna ulica i kućni broj. Koristi se u pravnim dokumentima (računima, ugovorima) i za map pin na Booking Engine-u ako lat/lng nije postavljen."
            >
              <input className="rw-input" value={form.address || ""} onChange={(e) => update("address", e.target.value)} />
            </L>
            <div className="grid grid-cols-2 gap-3">
              <L
                label="Širina (lat)"
                help="Preciznije od adrese za map pin na Booking Engine-u i buducoj OTA integraciji. Dohvati iz Google Mapsa desnim klikom na lokaciju → prvi broj."
              >
                <input type="number" step="0.000001" className="rw-input" value={form.latitude ?? ""} onChange={(e) => update("latitude", e.target.value ? parseFloat(e.target.value) : null)} placeholder="npr. 45.32711" />
              </L>
              <L
                label="Dužina (lng)"
                help="Drugi broj iz Google Mapsa (nakon zareza). Zajedno s lat prikazuje smještaj na karti."
              >
                <input type="number" step="0.000001" className="rw-input" value={form.longitude ?? ""} onChange={(e) => update("longitude", e.target.value ? parseFloat(e.target.value) : null)} placeholder="npr. 14.44262" />
              </L>
            </div>
          </section>

          {/* Marketing */}
          <section className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Marketing (Booking Engine + Javni sajt)</p>
            <L
              label="URL glavne slike"
              help="Kvadratna ili landscape fotografija smještaja koja se prikazuje kao hero na Booking Engine listi. Preporuka: ≥1200px, JPEG."
            >
              <input className="rw-input" value={form.main_image || ""} onChange={(e) => update("main_image", e.target.value)} placeholder="https://..." />
            </L>
            <p className="text-[10.5px] italic text-muted-foreground">
              Opisi (hr/en/de/it) uređuju se u Booking Engine tabu. Slika i opisi idu <b>samo u Booking Engine API</b> (vaša web stranica s API ključem) — ne na OTA kanale.
            </p>
          </section>

          {/* Vrijeme dolaska i odlaska */}
          <section className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Vrijeme dolaska i odlaska</p>
            <p className="rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800" data-testid="property-times-scope">
              Koristi se u automatskim porukama svim gostima ({"{check_in_time}"}, {"{check_out_time}"}) i u Booking Engine API-ju. <b>Ne šalje se na OTA</b> — na Booking.com / Airbnb listingu postavi isto vrijeme.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <L
                label="Check-in"
                help="Najraniji dolazak gosta. Prikazuje se u potvrdi rezervacije, e-mailu i računima. Standardno 15:00 u Hrvatskoj."
              >
                <input type="time" className="rw-input" value={form.check_in_time} onChange={(e) => update("check_in_time", e.target.value)} />
              </L>
              <L
                label="Check-out"
                help="Najkasniji odlazak. Nakon ovog vremena jedinica se prazni. Standardno 10:00–11:00 u Hrvatskoj."
              >
                <input type="time" className="rw-input" value={form.check_out_time} onChange={(e) => update("check_out_time", e.target.value)} />
              </L>
            </div>
          </section>

        </div>
        <div className="flex justify-end gap-2 border-t border-border px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">
          <button onClick={onClose} className="h-10 rounded-md border border-border px-4 text-sm font-medium hover:bg-muted">Odustani</button>
          <button onClick={submit} disabled={saving} className="h-10 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="prop-save">
            {saving ? "Spremam..." : isNew ? "Kreiraj" : "Spremi"}
          </button>
        </div>
      </div>
      <style>{`.rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); } .rw-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary)/0.2); }`}</style>
    </>
  );
}

export default function PropertiesPage() {
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [pr, ur] = await Promise.all([api.get("/properties"), api.get("/units")]);
      setItems(pr.data); setUnits(ur.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const unitCount = (pid) => units.filter((u) => u.property_id === pid).length;
  const channexMapping = (p) => (p.channel_mappings || []).find((m) => m.channel === "channex");

  const del = async (id) => {
    if (!window.confirm("Izbrisati smještaj i sve jedinice?")) return;
    await api.delete(`/properties/${id}`);
    refresh();
  };

  // F8.7: Move property up/down. Backend persists new display_order so Calendar
  // reflects the change immediately after refresh. We optimistically update
  // local state so UI responds without waiting for the request.
  const [reordering, setReordering] = useState(false);
  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);  // optimistic
    setReordering(true);
    try {
      await api.post("/properties/reorder", { property_ids: next.map((p) => p.id) });
      toast.success("Redoslijed spremljen");
    } catch (e) {
      toast.error(formatApiError(e));
      // Revert on failure
      refresh();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="properties-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{items.length} smještaja</p>
        <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="new-property-btn">
          <Plus size={16} weight="bold" /> Novi smještaj
        </button>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <Buildings size={36} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">Nema smještaja</p>
          <p className="mt-1 text-xs text-muted-foreground">Kliknite &quot;Novi smještaj&quot; za dodavanje.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p, index) => {
            const Icon = TYPE_ICONS[p.type] || House;
            const cm = channexMapping(p);
            return (
              <div key={p.id} className="overflow-hidden rounded-lg border border-border bg-card lift" data-testid={`prop-card-${p.id}`}>
                <div className="relative h-32 bg-muted">
                  {p.main_image ? (
                    <img src={p.main_image} alt={p.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center"><Icon size={32} className="text-muted-foreground" /></div>
                  )}
                  <span className="absolute left-2 top-2 rounded-full bg-card/90 px-2 py-0.5 text-xs font-medium text-foreground backdrop-blur">
                    {TYPE_LABELS[p.type] || p.type}
                  </span>
                  {cm && (
                    <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-success/90 px-2 py-0.5 text-xs font-medium text-white backdrop-blur" title="Aktivna sinkronizacija s OTA kanalima">
                      <Broadcast size={10} weight="fill" /> Sync
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground">{p.name}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{p.city || "-"} • {unitCount(p.id)} jedinica</p>
                  <div className="mt-3 flex items-center justify-between gap-1">
                    {/* F8.7: Reorder arrows — determine Calendar top-to-bottom order */}
                    <div className="flex items-center gap-0.5" data-testid={`prop-reorder-${p.id}`}>
                      <button
                        onClick={() => move(index, -1)}
                        disabled={reordering || index === 0}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
                        title="Pomakni gore (prikazuje se više u kalendaru)"
                        data-testid={`prop-move-up-${p.id}`}
                      >
                        <ArrowUp size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => move(index, 1)}
                        disabled={reordering || index === items.length - 1}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
                        title="Pomakni dolje"
                        data-testid={`prop-move-down-${p.id}`}
                      >
                        <ArrowDown size={13} weight="bold" />
                      </button>
                      <span className="ml-1 text-[10px] font-mono text-muted-foreground">#{index + 1}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(p)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`prop-edit-${p.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => del(p.id)} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid={`prop-del-${p.id}`}><Trash size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <PropertyForm
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => refresh()}
        />
      )}
    </div>
  );
}
